import { useEffect, useState } from 'react';
import { ChevronsLeftRightEllipsis, Key } from 'lucide-react';

import { env } from 'config/env';
import config, { ENV_TITLE_CASE } from 'config/constants';

import { generateAuthApiToken, trackSegmentEvent } from 'app/utils';
import { ACTION, SOURCE } from 'app/analytics';
import { useAppSelector } from 'app/store';
import { selectUserInfo, selectUserInfoLoading } from 'app/store/user';
import { selectClientIp } from 'app/store/clientIp';

import FormRequestBlock from 'components/FormRequestBlock';
import { CopyableField } from 'components/ui/copyable-field';
import { Typography } from 'components/ui/typography';

const ApiKey = () => {
  const { apiKey = '' } = useAppSelector(selectUserInfo) || {};
  const isUserInfoLoading = useAppSelector(selectUserInfoLoading);
  const clientIp = useAppSelector(selectClientIp) || '<< Insert IP address here >>';
  const [formLoading, setFormLoading] = useState(!apiKey);

  const handleOnSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    setFormLoading(!apiKey && isUserInfoLoading);
  }, [apiKey, isUserInfoLoading]);

  // The header body is base64(apiKey + ':'), so it is just as sensitive as
  // the key itself. Hide the body and keep the scheme + last 4 base64 chars
  // so the row is still recognisable.
  const maskAuthHeader = (token: string) => `Basic ••••••••${token.slice(-4)}`;

  return (
    <FormRequestBlock
      title={`${ENV_TITLE_CASE} Sync API Key Headers`}
      text={
        <>
          <p>
            These headers are required for authenticating GraphQL requests to Rye's {ENV_TITLE_CASE}{' '}
            Sync API.
          </p>
          <p>
            To learn how to get started, check out the{' '}
            <Typography variant="link" asChild>
              <a href={config.external.doc.quickStart} target="_blank" rel="noreferrer noopener">
                Quick Start Guide
              </a>
            </Typography>
            , or the guide on how to{' '}
            <Typography variant="link" asChild>
              <a
                href="https://docs.rye.com/place-test-orders"
                target="_blank"
                rel="noreferrer noopener"
              >
                place test orders{env.VITE_NODE_ENV === 'production' ? ' in Staging' : ''}
              </a>
            </Typography>
            .
          </p>
          <p>
            The GraphQL endpoint for the {env.VITE_NODE_ENV} environment is:{' '}
            <code>{config.external.gql}</code>.
          </p>
        </>
      }
      loading={formLoading}
      onSubmit={handleOnSubmit}
    >
      <div className="flex w-full flex-col gap-2">
        <CopyableField
          label="Authorization:"
          value={generateAuthApiToken(apiKey)}
          mask={apiKey ? maskAuthHeader : undefined}
          icon={<Key className="h-4 w-4" />}
          onCopy={() =>
            trackSegmentEvent(SOURCE.API_KEY_MODULE, ACTION.CLICK, 'copy_api_key_button')
          }
          copyButtonTitle="Copy Authorization token"
        />
        <div className="flex w-full items-center gap-2">
          <div className="flex w-full items-center gap-2 rounded-md border border-gray-200 p-2">
            <ChevronsLeftRightEllipsis className="h-4 w-4" />
            <h4 className="text-sm font-bold">Rye-Shopper-IP:</h4>
            <code className="text-sm text-gray-500">{clientIp}</code>
          </div>
        </div>
      </div>
    </FormRequestBlock>
  );
};

export default ApiKey;
